import { useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { LauncherBilan, ResultBilan } from "@/modules/launcher/Launcher";
import { LauncherExploration } from "@/modules/bilan/LauncherExploration";
import { LauncherLecture } from "@/modules/bilan/LauncherLecture";
import EcranInstructions from "../autres/EcranInstructions";
import axios from "axios";
import { saveResultsModules } from "@/utils/saveResultsModules";
import { PositionBilan } from "../bilan/Bilan";
import { useFullscreen } from "@mantine/hooks";

export interface SessionInfo {
  sujet: string;
  notes: string;
  targetEnCours_Zf_Bilan: PositionBilan[] | null;
  status: "EN_COURS" | "PREVUE" | "REALISEE";
  resultsBilan: ResultBilan[] | null;
  resultsExplorationIsRealised: boolean; /// true si exploration réalisée, false si non
  resultsBilanLectureIsRealised: boolean; /// true si lecture réalisée, false si non
  parameters: {
    Bilan_Cv: ModuleParameters;
    Bilan_Lecture: ModuleBilanLectureParameters;
    Bilan_Exploration: ModuleParameters;
  };
}

interface ModuleParameters {
  selected: boolean;
  sous_module: string;
}

interface ModuleBilanLectureParameters {
  contenu: string;
  selected: boolean;
  sous_module: string;
}

const ModuleLauncherBilan = ({
  role,
}: {
  role: "practitioner" | "patient";
}) => {
  const { sessionId } = useParams();
  const [isCursorVisible, setIsCursorVisible] = useState(false);
  const [sessionBilanInfo, setSessionBilanInfo] = useState<SessionInfo>({
    sujet: "",
    notes: "",
    targetEnCours_Zf_Bilan: null,
    resultsBilan: null,
    resultsExplorationIsRealised: false,
    resultsBilanLectureIsRealised: false,
    status: "PREVUE",
    parameters: {
      Bilan_Cv: { selected: false, sous_module: "Bilan_Cv" },
      Bilan_Lecture: {
        contenu: "1",
        selected: false,
        sous_module: "Bilan_Lecture",
      },
      Bilan_Exploration: { selected: false, sous_module: "Bilan_Exploration" },
    },
  });

  const { toggle: toggleFullscreen } = useFullscreen();
  const caseSizeName = localStorage.getItem("selectedCaseSizeName");

  useEffect(() => {
    toggleFullscreen();
  }, []);

  useEffect(() => {
    if (sessionId) {
      axios
        .get(`/${role}/session/${sessionId}`)
        .then((response) => {
          const sessionData = response.data;
          const adaptedParameters = sessionData.parameters.reduce(
            (
              acc: { [key: string]: ModuleParameters },
              param: { content: string }
            ) => {
              const content = JSON.parse(param.content);
              const key = content.sous_module;
              acc[key] = { ...acc[key], ...content };
              return acc;
            },
            {}
          );

          setSessionBilanInfo({
            parameters: adaptedParameters,
            sujet: sessionData.sujet,
            notes: sessionData.notes,
            targetEnCours_Zf_Bilan: JSON.parse(
              sessionData.targetEnCours_Zf_Bilan
            ),
            status: sessionData.status,
            resultsBilan: JSON.parse(sessionData.resultsBilan),
            resultsExplorationIsRealised: sessionData.resultsExploration
              ? true
              : false,
            resultsBilanLectureIsRealised: sessionData.resultsBilanLecture
              ? true
              : false,
          });
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la récupération des données de la séance:",
            error
          );
        });
    }
  }, [sessionId, role]);

  const [currentModule, setCurrentModule] = useState<
    "Bilan" | "Exploration" | "Lecture" | null
  >(null);
  const handleNextModule = useCallback(() => {
    if (sessionBilanInfo.status === "PREVUE") {
      if (
        currentModule === "Bilan" &&
        sessionBilanInfo.parameters.Bilan_Exploration.selected
      ) {
        setCurrentModule("Exploration");
      } else if (
        currentModule === "Exploration" &&
        sessionBilanInfo.parameters.Bilan_Lecture.selected
      ) {
        setCurrentModule("Lecture");
      } else {
        if (areModulesSelectioned) {
          realizeSession();
        }
        setCurrentModule(null); // Tous les exercices sont terminés
      }
    } else if (sessionBilanInfo.status === "EN_COURS") {
      if (
        currentModule === "Bilan" &&
        sessionBilanInfo.parameters.Bilan_Exploration.selected &&
        !sessionBilanInfo.resultsExplorationIsRealised
      ) {
        setCurrentModule("Exploration");
      } else if (
        currentModule === "Exploration" &&
        sessionBilanInfo.parameters.Bilan_Lecture.selected &&
        !sessionBilanInfo.resultsBilanLectureIsRealised
      ) {
        setCurrentModule("Lecture");
      } else {
        if (areModulesSelectioned) {
          realizeSession();
        }
        setCurrentModule(null); // Tous les exercices sont terminés
      }
    }
  }, [currentModule, sessionBilanInfo]);

  const saveFieldSession = useCallback(
    (
      fieldsToUpdate: {
        field: string;
        value: string | number | number[] | Date | null;
      }[]
    ) => {
      try {
        saveResultsModules(fieldsToUpdate, sessionId!, role).catch((error) => {
          console.error(
            "An error occurred while updating session status:",
            error
          );
        });
      } catch (error) {
        console.error(
          "An error occurred while preparing session status update:",
          error
        );
      }
    },
    [sessionId, role]
  );

  useEffect(() => {
    const handleKeyPress = async (e: { key: string }) => {
      if (e.key === "ArrowRight") {
        handleNextModule();
      }
    };
    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleNextModule]);

  useEffect(() => {
    // Lancer le premier exercice sélectionné
    if (sessionBilanInfo.status === "PREVUE") {
      if (sessionBilanInfo.parameters.Bilan_Cv.selected) {
        setCurrentModule("Bilan");
      } else if (sessionBilanInfo.parameters.Bilan_Exploration.selected) {
        setCurrentModule("Exploration");
      } else if (sessionBilanInfo.parameters.Bilan_Lecture.selected) {
        setCurrentModule("Lecture");
      } else {
        if (areModulesSelectioned) {
          realizeSession();
        }
        setCurrentModule(null); // Aucun exercice sélectionné
      }
    } else if (sessionBilanInfo.status === "EN_COURS") {
      if (
        (sessionBilanInfo.parameters.Bilan_Cv.selected &&
          !sessionBilanInfo.resultsBilan) ||
        (sessionBilanInfo.parameters.Bilan_Cv.selected &&
          sessionBilanInfo.targetEnCours_Zf_Bilan !== null)
      ) {
        setCurrentModule("Bilan");
      } else if (
        sessionBilanInfo.parameters.Bilan_Exploration.selected &&
        !sessionBilanInfo.resultsExplorationIsRealised
      ) {
        setCurrentModule("Exploration");
      } else if (
        sessionBilanInfo.parameters.Bilan_Lecture.selected &&
        !sessionBilanInfo.resultsBilanLectureIsRealised
      ) {
        setCurrentModule("Lecture");
      } else {
        if (areModulesSelectioned) {
          realizeSession();
        }
        setCurrentModule(null);
      }
    }
  }, [sessionBilanInfo]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      setIsCursorVisible(!isFullscreen);
    };

    window.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      window.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const cursorClassVisibility =
    isCursorVisible || !currentModule ? "" : "cursor-none";


  const startSession = useCallback(() => {
    const fieldsToUpdate = [
      { field: "status", value: "EN_COURS" },
      { field: "sessionDate", value: new Date().toISOString() },
      { field: "screenUsed", value: String(caseSizeName) },
      {
        field: "accountUsed",
        value: role === "practitioner" ? "CLINIQUE" : "PATIENT",
      },
    ];

    try {
      saveResultsModules(fieldsToUpdate, sessionId!, role).catch((error) => {
        console.error(
          "An error occurred while updating session status:",
          error
        );
      });
    } catch (error) {
      console.error(
        "An error occurred while preparing session status update:",
        error
      );
    }
  }, [sessionId, role]);

  const realizeSession = useCallback(() => {
    const fieldsToUpdate = [
      { field: "sessionDate", value: new Date().toISOString() },
      { field: "status", value: "REALISEE" },
    ];
    try {
      // Enregistrer les résultats
      saveResultsModules(fieldsToUpdate, sessionId!, role)
        .catch((error) => {
          console.error("An error occurred while saving results:", error);
        });
    } catch (error) {
      console.error("An error occurred while preparing the save:", error);
    }
  }, [caseSizeName, sessionId, role]);

  const currentModuleIndexBilan = sessionBilanInfo.parameters.Bilan_Cv
    .selected
    ? 0
    : 1;

  const areModulesSelectioned =
    sessionBilanInfo.parameters.Bilan_Cv.selected ||
    sessionBilanInfo.parameters.Bilan_Exploration.selected ||
    sessionBilanInfo.parameters.Bilan_Lecture.selected;

  return (
    <div>
      {areModulesSelectioned ? (
        <div className={cursorClassVisibility}>
          {currentModule === "Bilan" && (
            <LauncherBilan
              onModuleEnd={handleNextModule}
              role={role}
              saveFieldSession={saveFieldSession}
              startSession={startSession}
              currentModuleIndex={currentModuleIndexBilan}
              status={sessionBilanInfo.status}
              targetEnCours_Zf_Bilan={sessionBilanInfo.targetEnCours_Zf_Bilan}
              resultsBilan={sessionBilanInfo.resultsBilan}
            />
          )}
          {currentModule === "Exploration" && (
            <LauncherExploration
              onModuleEnd={handleNextModule}
              saveFieldSession={saveFieldSession}
            />
          )}
          {currentModule === "Lecture" && (
            <LauncherLecture
              onModuleEnd={handleNextModule}
              TextNumber={Number(
                sessionBilanInfo.parameters.Bilan_Lecture.contenu
              )}
              saveFieldSession={saveFieldSession}
            />
          )}
          {/* {!currentModule && <p>Exercices terminés.</p>} */}
          {!currentModule && (
            <EcranInstructions
              instructions="Exercices terminés."
              redirectHome={true}
            />
          )}
        </div>
      ) : (
        "Aucun module n'a été séléctionné"
      )}
    </div>
  );
};

export default ModuleLauncherBilan;
